import { apiClient } from './api-client';
import { useAuthStore } from '@/stores';
import { invalidateLocalPowerCache } from './edge-client';
import { getMockPowerOverview, getMockPowerSubscribers } from './mockPowerData';
import { realPowerMap, getRealPowerRecord, REAL_POWER_SUBSCRIBERS, populateMap } from './realPowerData';

// ===================================================
// AUTH SERVICE
// Token is fetched via fetchFTTHToken() in api-client.ts
// These use standard /api endpoints
// ===================================================
export const authService = {
  // Get current logged-in user profile
  getCurrentUser: () => {
    const authState = useAuthStore.getState();
    if (authState.user?.authType === 'LOCAL') {
      return apiClient.get('/auth/me');
    }
    return apiClient.get('/current-user');
  },

  // Logout (client-side only - clear tokens)
  logout: () => Promise.resolve({ data: null }),

  // Get system configurations
  getConfigurations: () => apiClient.get('/configurations'),
};

// ===================================================
// CUSTOMER SERVICE
// ===================================================
export const customerService = {
  /**
   * List customers with pagination
   * GET /api/customers
   */
  list: (params?: {
    pageSize?: number;
    pageNumber?: number;
    'sortCriteria.property'?: string;
    'sortCriteria.direction'?: string;
    name?: string;
    phone?: string;
    customerId?: string;
    email?: string;
    ontSerial?: string;
    zoneId?: string;
    zone?: string;
    hierarchyLevel?: string | number;
    additionalFilter?: string;
  }) =>
    apiClient.get('/customers', {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': params?.['sortCriteria.property'] ?? 'self.displayValue',
      'sortCriteria.direction': params?.['sortCriteria.direction'] ?? 'asc',
      ...(params?.name ? { name: params.name } : {}),
      ...(params?.phone ? { phone: params.phone } : {}),
      ...(params?.customerId ? { customerId: params.customerId } : {}),
      ...(params?.email ? { email: params.email } : {}),
      ...(params?.ontSerial ? { ontSerial: params.ontSerial } : {}),
      ...(params?.zoneId ? { zoneId: params.zoneId } : {}),
      ...(params?.zone ? { zone: params.zone } : {}),
      ...(params?.hierarchyLevel !== undefined ? { hierarchyLevel: params.hierarchyLevel } : {}),
      ...(params?.additionalFilter ? { additionalFilter: params.additionalFilter } : {}),
    }),

  /**
   * Get customer summary
   * GET /api/customers/summary
   */
  summary: (params?: { pageSize?: number; pageNumber?: number }) =>
    apiClient.get('/customers/summary', {
      partnersAndLOBAgnostic: false,
      pageSize: params?.pageSize ?? 50,
      pageNumber: params?.pageNumber ?? 1,
    }),

  /**
   * Get customer summaries by customerIds
   * GET /api/customers/summary?customerIds=...
   */
  summaryByIds: (customerIds: (number | string)[]) => {
    const params = new URLSearchParams();
    customerIds.forEach((id) => params.append('customerIds', String(id)));
    return apiClient.get(`/customers/summary?${params.toString()}`);
  },

  /**
   * Get account addresses by multiple accountIds
   * GET /api/addresses?accountIds=...&accountIds=...
   */
  addresses: (accountIds: (number | string)[]) => {
    const params = new URLSearchParams();
    accountIds.forEach((id) => params.append('accountIds', String(id)));
    return apiClient.get(`/addresses?${params.toString()}`);
  },

  /**
   * Get customer details by ID
   * GET /api/customers/:id
   */
  get: (id: string) => apiClient.get(`/customers/${id}`),

  /**
   * Get customer subscriptions
   * GET /api/customers/subscriptions?customerId=:customerId
   */
  subscriptions: (customerId: string) => apiClient.get('/customers/subscriptions', { customerId }),

  /**
   * Get customer tasks
   * GET /api/customers/:id/tasks
   */
  tasks: (customerId: string) => apiClient.get(`/customers/${customerId}/tasks`),

  /**
   * Get customer scheduled changes
   * GET /api/customers/:id/subscriptions/scheduled-changes
   */
  scheduledChanges: (customerId: string) => apiClient.get(`/customers/${customerId}/subscriptions/scheduled-changes`),
};

// ===================================================
// SUBSCRIPTION SERVICE
// ===================================================
export const subscriptionService = {
  /**
   * List subscriptions
   * GET /api/subscriptions
   */
  list: (params?: {
    pageSize?: number;
    pageNumber?: number;
    'sortCriteria.property'?: string;
    'sortCriteria.direction'?: string;
    hierarchyLevel?: number;
  }) =>
    apiClient.get('/subscriptions', {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': params?.['sortCriteria.property'] ?? 'expires',
      'sortCriteria.direction': params?.['sortCriteria.direction'] ?? 'asc',
      hierarchyLevel: params?.hierarchyLevel ?? 0,
    }),

  /**
   * Get all possible subscription statuses
   * GET /api/subscriptions/statuses
   */
  statuses: () => apiClient.get('/subscriptions/statuses'),

  /**
   * Get allowed actions for multiple subscriptions
   * GET /api/subscriptions/allowed-actions?subscriptionIds=...
   */
  allowedActions: (subscriptionIds: (number | string)[], customerId?: string) => {
    const params = new URLSearchParams();
    subscriptionIds.forEach((id) => params.append('subscriptionIds', String(id)));
    if (customerId) {
      params.append('customerId', customerId);
    }
    return apiClient.get(`/subscriptions/allowed-actions?${params.toString()}`);
  },

  /**
   * Suspend a subscription
   * POST /api/subscriptions/:id/suspend
   */
  suspend: (subscriptionId: string | number) => 
    apiClient.post(`/subscriptions/${subscriptionId}/suspend`),

  /**
   * Unsuspend a subscription
   * POST /api/subscriptions/:id/unsuspend
   */
  unsuspend: (subscriptionId: string | number) => 
    apiClient.post(`/subscriptions/${subscriptionId}/unsuspend`),

  /**
   * Get subscription device details
   * GET /api/subscriptions/:id/device
   */
  getDevice: (subscriptionId: string | number) => 
    apiClient.get(`/subscriptions/${subscriptionId}/device`),

  /**
   * Update subscription device details (FDT, FAT, ONT Vendor, ONT Serial, Password, Locked MAC)
   * PUT /api/subscriptions/:id/device
   */
  updateDevice: (subscriptionId: string | number, data: any) =>
    apiClient.put(`/subscriptions/${subscriptionId}/device`, data),

  /**
   * Get single subscription details
   * GET /api/subscriptions/:id
   */
  getSubscription: (subscriptionId: string | number) =>
    apiClient.get(`/subscriptions/${subscriptionId}`),

  /**
   * Validate subscription password
   * POST /api/subscriptions/validate-password
   */
  validatePassword: (password: string) =>
    apiClient.post('/subscriptions/validate-password', { password }),

  /**
   * Get subscription device OLT status and Rx Power
   * GET /api/subscriptions/:id/device/ont or GET /api/subscriptions/device/ont?username=:username
   */
  getDeviceOnt: (subscriptionId: string | number) => 
    apiClient.get(`/subscriptions/${subscriptionId}/device/ont`),

  getDeviceOntStatus: (username: string) =>
    apiClient.get('/subscriptions/device/ont', { username }),


  /**
   * Get subscription sessions total usage
   * GET /api/subscriptions/:id/sessions/total-usage
   */
  getTotalUsage: (subscriptionId: string | number) => 
    apiClient.get(`/subscriptions/${subscriptionId}/sessions/total-usage`),

  /**
   * Get subscription connection errors
   * GET /api/subscriptions/:id/errors
   */
  getErrors: (subscriptionId: string | number) => 
    apiClient.get(`/subscriptions/${subscriptionId}/errors`),
};

// ===================================================
// REQUEST SERVICE
// ===================================================
export const requestService = {
  /**
   * List requests
   * GET /api/requests
   */
  list: (params?: {
    pageSize?: number;
    pageNumber?: number;
    'sortCriteria.property'?: string;
    'sortCriteria.direction'?: string;
    typeIds?: string;
    status?: string;
    additionalFilters?: string[];
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.set('pageSize', String(params?.pageSize ?? 10));
    queryParams.set('pageNumber', String(params?.pageNumber ?? 1));
    queryParams.set('sortCriteria.property', params?.['sortCriteria.property'] ?? 'CreatedAt');
    queryParams.set('sortCriteria.direction', params?.['sortCriteria.direction'] ?? 'desc');
    if (params?.typeIds) queryParams.set('typeIds', params.typeIds);
    if (params?.status) queryParams.set('status', params.status);
    
    queryParams.append('additionalFilters', 'MyPartnersRequests');
    queryParams.append('additionalFilters', 'MyRequests');

    if (params?.additionalFilters && Array.isArray(params.additionalFilters)) {
      params.additionalFilters.forEach((f) => {
        if (f && f !== 'MyPartnersRequests' && f !== 'MyRequests') {
          queryParams.append('additionalFilters', f);
        }
      });
    }

    return apiClient.get(`/requests?${queryParams.toString()}`);
  },

  /**
   * Get available request types
   * GET /api/requests/types
   */
  types: () => apiClient.get('/requests/types'),

  /**
   * Get all request statuses
   * GET /api/requests/statuses
   */
  statuses: () => apiClient.get('/requests/statuses'),

  /**
   * Request summary
   * GET /api/requests/summary?hierarchyLevel=0
   */
  summary: () => apiClient.get('/requests/summary', { hierarchyLevel: 0 }),

  /**
   * Get customer onboarding request details
   * GET /api/requests/customer-onboarding/:id
   */
  customerOnboarding: (id: string) => apiClient.get(`/requests/customer-onboarding/${id}`),

  /**
   * Get comments for a request
   * GET /api/requests/:id/comments
   */
  comments: (id: string, params?: { pageSize?: number; pageNumber?: number }) =>
    apiClient.get(`/requests/${id}/comments?pageSize=${params?.pageSize ?? 10}&pageNumber=${params?.pageNumber ?? 1}`),

  /**
   * Add comment to a request
   * POST /api/requests/:id/comments
   */
  addComment: (id: string, content: string) =>
    apiClient.post(`/requests/${id}/comments`, { content }),

  /**
   * Get audit logs for a request
   * GET /api/requests/:id/logs
   */
  logs: (id: string) => apiClient.get(`/requests/${id}/logs`),
};

// ===================================================
// TASK SERVICE
// ===================================================
export const taskService = {
  /**
   * List tasks for given request IDs
   * GET /api/tasks?status=0&requestIds=...
   */
  list: (params?: { requestIds?: string[]; status?: number }) => {
    if (params?.requestIds && params.requestIds.length > 0) {
      const queryParams = new URLSearchParams();
      if (params.status !== undefined) queryParams.set('status', String(params.status));
      params.requestIds.forEach((id) => queryParams.append('requestIds', id));
      return apiClient.get(`/tasks?${queryParams.toString()}`);
    }
    return apiClient.get('/tasks', { hierarchyLevel: 0 });
  },

  /**
   * Task summary
   * GET /api/tasks/summary?hierarchyLevel=0
   */
  summary: () => apiClient.get('/tasks/summary', { hierarchyLevel: 0 }),
};

// ===================================================
// WALLET SERVICE
// ===================================================
export const walletService = {
  /**
   * Get wallet balance for a partner
   * GET /api/partners/{partnerId}/wallets/balance
   */
  balance: (partnerId: number | string) =>
    apiClient.get(`/partners/${partnerId}/wallets/balance`),

  /**
   * Get financial transactions
   * GET /api/transactions
   */
  transactions: (params?: {
    pageSize?: number;
    pageNumber?: number;
    partnerId?: number | string;
    walletType?: string;
    hierarchyLevel?: number;
    walletOwnerType?: string;
    'sortCriteria.property'?: string;
    'sortCriteria.direction'?: string;
  }) =>
    apiClient.get('/transactions', {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': params?.['sortCriteria.property'] ?? 'occuredAt',
      'sortCriteria.direction': params?.['sortCriteria.direction'] ?? 'desc',
      walletType: params?.walletType ?? 'Main',
      hierarchyLevel: params?.hierarchyLevel ?? 1,
      walletOwnerType: params?.walletOwnerType ?? 'Partner',
      ...(params?.partnerId ? { partnerId: params.partnerId } : {}),
    }),

  /**
   * Get payment methods for subscriptions
   * GET /api/transactions/payment-methods?subscriptionsIds=...
   */
  paymentMethods: (subscriptionsIds: (number | string)[]) => {
    const params = new URLSearchParams();
    subscriptionsIds.forEach((id) => params.append('subscriptionsIds', String(id)));
    return apiClient.get(`/transactions/payment-methods?${params.toString()}`);
  },
};

// ===================================================
// DASHBOARD SERVICE
// ===================================================
export const dashboardService = {
  /**
   * Partner dashboard summary
   * GET /api/partners/dashboard/summary?hierarchyLevel=0
   */
  summary: () => apiClient.get('/partners/dashboard/summary', { hierarchyLevel: 0 }),

  /**
   * Tasks summary
   * GET /api/tasks/summary?hierarchyLevel=0
   */
  tasksSummary: () => apiClient.get('/tasks/summary', { hierarchyLevel: 0 }),

  /**
   * Support tickets summary
   * GET /api/support/tickets/summary?hierarchyLevel=0
   */
  supportTickets: (params?: { hierarchyLevel?: number; forceRefresh?: boolean }) =>
    apiClient.get('/support/tickets/summary', {
      hierarchyLevel: params?.hierarchyLevel ?? 0,
      ...(params?.forceRefresh ? { forceRefresh: true, _t: Date.now() } : {})
    }),

  /**
   * Support tickets statuses
   * GET /api/support/tickets/statuses
   */
  supportTicketsStatuses: () => apiClient.get('/support/tickets/statuses'),

  /**
   * Support tickets categories
   * GET /api/support/tickets/categories
   */
  supportTicketsCategories: () => apiClient.get('/support/tickets/categories'),

  /**
   * Support tickets list
   * GET /api/support/tickets
   */
  supportTicketsList: (params?: {
    pageSize?: number;
    pageNumber?: number;
    'sortCriteria.property'?: string;
    'sortCriteria.direction'?: string;
    hierarchyLevel?: number;
    additionalFilter?: string;
    statusId?: string;
    displayId?: string;
    customerName?: string;
    forceRefresh?: boolean;
  }) =>
    apiClient.get('/support/tickets', {
      pageSize: params?.pageSize ?? 12,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': params?.['sortCriteria.property'] ?? 'createdAt',
      'sortCriteria.direction': params?.['sortCriteria.direction'] ?? 'desc',
      hierarchyLevel: params?.hierarchyLevel ?? 0,
      additionalFilter: params?.additionalFilter ?? 'ForMe',
      statusId: params?.statusId,
      displayId: params?.displayId,
      customerName: params?.customerName,
      ...(params?.forceRefresh ? { forceRefresh: true, _t: Date.now() } : {})
    }),

  /**
   * Get support ticket details
   * GET /api/support/tickets/:id
   */
  supportTicketDetails: (id: string) =>
    apiClient.get(`/support/tickets/${id}`),

  /**
   * Get support ticket comments/discussion
   * GET /api/support/tickets/:id/comments
   */
  supportTicketComments: (id: string, params?: { pageSize?: number; pageNumber?: number }) =>
    apiClient.get(`/support/tickets/${id}/comments`, {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
    }),

  /**
   * Submit support ticket comment
   * POST /api/support/tickets/:id/comments
   */
  createTicketComment: (id: string, content: string) =>
    apiClient.post(`/support/tickets/${id}/comments`, { content }),

  /**
   * Requests summary
   * GET /api/requests/summary?hierarchyLevel=0
   */
  requestsSummary: () => apiClient.get('/requests/summary', { hierarchyLevel: 0 }),
};

// ===================================================
// PLANS SERVICE
// ===================================================
export const planService = {
  /**
   * Get available service bundles/plans
   * GET /api/plans/bundles?includePrices=true
   */
  bundles: (includePrices = true) =>
    apiClient.get('/plans/bundles', { includePrices }),
};

// ===================================================
// LOCATION & NETWORK ELEMENTS SERVICE
// ===================================================
export const locationService = {
  /**
   * Get all available coverage zones
   * GET /api/locations/zones
   */
  zones: () => apiClient.get('/locations/zones'),

  /**
   * Get FDTs by Zone ID from portal
   * GET /api/network-elements/fdts?zoneId={zoneId}
   */
  getFdtsByZone: (zoneId: string) => apiClient.get('/network-elements/fdts', { zoneId }),

  /**
   * Get FATs by FDT ID from portal
   * GET /api/network-elements/fats?fdtId={fdtId}
   */
  getFatsByFdt: (fdtId: string) => apiClient.get('/network-elements/fats', { fdtId }),
};

// ===================================================
// CONFIGURATION SERVICE
// ===================================================
export const configurationService = {
  /**
   * Get system configurations
   * GET /api/configurations
   */
  list: () => apiClient.get('/configurations'),
};

// ===================================================
// NOTIFICATION SERVICE
// ===================================================
export const notificationService = {
  /**
   * Send email notification for support tickets
   * POST /api/notifications/send-email
   */
  sendEmail: (data: {
    email: string;
    subject: string;
    ticketId: string;
    customerName: string;
    priority: string;
  }) => apiClient.post('/notifications/send-email', data),

  /**
   * Get notification user logs
   * GET /api/notifications/user-logs
   */
  getUserLogs: (params?: { type?: string }) => apiClient.get('/notifications/user-logs', params),

  /**
   * Mark notification as read or delete logs
   * POST /api/notifications/mark-read
   */
  markRead: (params: { notificationId?: string; all?: boolean; delete?: boolean; type?: string }) =>
    apiClient.post('/notifications/mark-read', params),
};


// ===================================================
// SESSIONS SERVICE
// ===================================================
export const sessionsService = {
  /**
   * Get active sessions from National Portal
   * GET /api/sessions (REQUEST #11 from API docs)
   */
  list: (params?: {
    pageSize?: number;
    pageNumber?: number;
    location?: string;
  }) =>
    apiClient.get('/sessions', {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': 'username',
      'sortCriteria.direction': 'asc',
      hierarchyLevel: 0,
      location: params?.location,
    }),

  /**
   * Terminate/delete an active session
   * DELETE /api/sessions/:id
   */
  terminate: (id: string) => apiClient.delete(`/sessions/${id}`),
};

// ===================================================
// SUBSCRIBER SESSIONS SERVICE (FTTH Portal Sessions)
// ===================================================
export const subscriberSessionsService = {
  /**
   * Get subscriber PPPoE/ONT sessions from FTTH Portal
   * GET /api/subscriber-sessions (proxied to /api/sessions on admin.ftth.iq)
   */
  list: (params?: {
    pageSize?: number;
    pageNumber?: number;
    status?: number; // 0 = Online, 1 = Offline
    username?: string; // Device / PPPoE username
    hierarchyLevel?: number; // 0 or 1
    sortCriteriaProperty?: string;
    sortCriteriaDirection?: string;
    zone?: string;
    datePreset?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const query: Record<string, any> = {
      pageSize: params?.pageSize ?? 10,
      pageNumber: params?.pageNumber ?? 1,
      'sortCriteria.property': params?.sortCriteriaProperty || 'username',
      'sortCriteria.direction': params?.sortCriteriaDirection || 'asc',
      hierarchyLevel: params?.hierarchyLevel ?? 0,
    };
    if (params?.status !== undefined && params?.status !== null) {
      query.status = params.status;
    }
    if (params?.username && params.username.trim() !== '') {
      query.username = params.username.trim();
    }
    if (params?.zone && params.zone !== 'all') {
      query.zone = params.zone;
    }
    if (params?.startDate) {
      query.startDate = params.startDate;
    }
    if (params?.endDate) {
      query.endDate = params.endDate;
    }
    return apiClient.get('/subscriber-sessions', query);
  },
};

export const ticketsService = dashboardService;

export function buildFatGroupsFromSubs(subs: any[], currentUserId: string = 'user'): any[] {
  const fatGroups: Record<string, any> = {};
  for (const sub of subs) {
    const fatKey = `${sub.fdt_code}:${sub.fat_code}`;
    if (!fatGroups[fatKey]) {
      fatGroups[fatKey] = {
        agent_name: sub.agent_name || "المقاول",
        agent_id: currentUserId,
        zone_code: sub.zone_code || "FNS",
        fdt_code: sub.fdt_code,
        fat_code: sub.fat_code,
        subscribers: 0,
        active_subscribers: 0,
        expired_subscribers: 0,
        connected_sessions: 0,
        readings_count: 0,
        min_power: null,
        max_power: null,
        normal_count: 0,
        abnormal_count: 0,
        down_count: 0,
        scannable_count: 0,
        updated_at: new Date().toISOString()
      };
    }
    const g = fatGroups[fatKey];
    g.subscribers++;
    if (sub.status === 'Active') {
      g.active_subscribers++;
      g.scannable_count++;
    } else {
      g.expired_subscribers++;
    }
    if (sub.has_active_session) g.connected_sessions++;

    const rxPower = sub.optical_rx_power;
    if (rxPower && rxPower !== 'Offline' && rxPower !== 'N/A' && rxPower !== 'Scanning...') {
      const p = parseFloat(rxPower);
      if (!isNaN(p)) {
        g.readings_count++;
        if (g.min_power === null || p < g.min_power) g.min_power = p;
        if (g.max_power === null || p > g.max_power) g.max_power = p;
        if (p <= -30.0) g.abnormal_count++;
        else g.normal_count++;
      }
    } else if (rxPower === 'Offline' && sub.status === 'Active') {
      g.down_count++;
    }
  }

  return Object.values(fatGroups).map((g: any) => ({
    ...g,
    subscribers: String(g.subscribers),
    active_subscribers: String(g.active_subscribers),
    expired_subscribers: String(g.expired_subscribers),
    connected_sessions: String(g.connected_sessions),
    readings_count: String(g.readings_count),
    min_power: g.min_power !== null ? String(g.min_power) : null,
    max_power: g.max_power !== null ? String(g.max_power) : null,
    output_power: g.max_power !== null ? String(g.max_power) : null,
    normal_count: String(g.normal_count),
    abnormal_count: String(g.abnormal_count),
    high_count: '0',
    down_count: String(g.down_count),
    diagnosis: g.down_count > 0 || g.abnormal_count > 0 ? 'critical' : 'normal',
    last_scan_started_at: new Date().toISOString(),
    last_scan_finished_at: new Date().toISOString(),
    last_scan_status: 'completed',
    last_scan_error: null,
    last_scan_scanned: g.scannable_count,
    last_scan_down: g.down_count,
    last_scan_offline: g.expired_subscribers,
    last_scan_failed: 0
  }));
}

// ===================================================

function parseFdtFatFromUsername(username: string = '', zoneCode: string = ''): { fdtCode: string; fatCode: string } {
  const u = (username || '').toLowerCase().trim();
  const z = (zoneCode || '').toUpperCase().trim();

  let zoneNum = '';
  const zMatch = z.match(/FNS0*(\d+)/i);
  if (zMatch) {
    zoneNum = String(parseInt(zMatch[1], 10));
  }

  let fdtNum = zoneNum;
  let fatNum = '1';

  const m1 = u.match(/(?:fd|fdt|fns|fa)0*(\d+)[-_]?(?:fa|fat)0*(\d+)/i);
  if (m1) {
    fdtNum = String(parseInt(m1[1], 10));
    fatNum = String(parseInt(m1[2], 10));
  } else {
    const mFat = u.match(/(?:fat|fa)0*(\d+)/i);
    if (mFat) fatNum = String(parseInt(mFat[1], 10));

    const mFdt = u.match(/(?:fdt|fd|fns)0*(\d+)/i);
    if (mFdt) fdtNum = String(parseInt(mFdt[1], 10));
  }

  if (!fdtNum) fdtNum = '28';
  const fdtCode = `FNS${fdtNum}`;
  const fatCode = `FAT${fatNum.padStart(2, '0')}`;

  return { fdtCode, fatCode };
}

// Single-Flight & 30s in-memory cache (Phase 4)
let portalPowerFetchPromise: Promise<{ subscribers: any[]; fats: any[] }> | null = null;
let portalPowerCache: { subscribers: any[]; fats: any[] } | null = null;
let portalPowerCacheAt = 0;

type PowerProgressCallback = (loaded: number, total: number, message?: string) => void;
const powerProgressListeners = new Set<PowerProgressCallback>();

export function onPowerFetchProgress(cb: PowerProgressCallback) {
  powerProgressListeners.add(cb);
  return () => { powerProgressListeners.delete(cb); };
}

function notifyPowerFetchProgress(loaded: number, total: number, message?: string) {
  powerProgressListeners.forEach(cb => {
    try { cb(loaded, total, message); } catch (e) {}
  });
}

async function fetchPortalPowerData(): Promise<{ subscribers: any[]; fats: any[] }> {
  const { user } = useAuthStore.getState();
  const activePartnerId = user?.partnerId ? String(user.partnerId) : (user?.username || 'user');
  const cacheKey = `nexus_power_inventory_${activePartnerId}`;

  const now = Date.now();
  if (portalPowerCache && (now - portalPowerCacheAt) < 30000 && portalPowerCache.subscribers.length > 500) {
    return portalPowerCache;
  }

  // Check persistent localStorage cache for instant zero-latency load
  if (!portalPowerCache && typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed?.subscribers) && parsed.subscribers.length > 500) {
          portalPowerCache = parsed;
          portalPowerCacheAt = now;
        }
      }
    } catch (e) {}
  }

  if (portalPowerFetchPromise) {
    return portalPowerFetchPromise;
  }

  portalPowerFetchPromise = (async () => {
    try {
      if (realPowerMap.size === 0 && typeof window !== 'undefined') {
        try {
          const resp = await fetch(`${window.location.origin}/real_power_subscribers.json`);
          if (resp.ok) {
            const json = await resp.json();
            if (Array.isArray(json?.subscribers)) {
              populateMap(json.subscribers);
            }
          }
        } catch (e) {}
      }

      const PAGE_SIZE = 100;
      notifyPowerFetchProgress(0, 0, 'جاري الاتصال بالبوابة وجلب بيانات المشتركين...');

      const firstRes = await subscriptionService.list({
        pageSize: PAGE_SIZE,
        pageNumber: 1,
        hierarchyLevel: 0,
        'sortCriteria.property': 'expires',
        'sortCriteria.direction': 'desc'
      }).catch(() => null);

      const firstData = firstRes?.data;
      const firstItems = firstData?.items || (Array.isArray(firstData) ? firstData : []);
      const totalCount = Number(firstData?.totalCount ?? firstItems.length);
      const resolvedPageSize = Number(firstData?.pageSize ?? PAGE_SIZE);
      const totalPages = Math.max(1, Math.ceil(totalCount / (resolvedPageSize || PAGE_SIZE)));

      const pageMap = new Map<number, any[]>();
      pageMap.set(1, firstItems);

      let totalLoaded = firstItems.length;
      notifyPowerFetchProgress(totalLoaded, totalCount, `تم جلب ${totalLoaded} من ${totalCount} مشترك...`);

      const fetchPageWithRetry = async (page: number, pageSize: number, retries = 3): Promise<any[]> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const pageRes = await subscriptionService.list({
              pageSize: pageSize,
              pageNumber: page,
              hierarchyLevel: 0,
              'sortCriteria.property': 'expires',
              'sortCriteria.direction': 'desc'
            });
            const data = pageRes?.data;
            const items = data?.items || (Array.isArray(data) ? data : []);
            if (Array.isArray(items) && items.length > 0) {
              return items;
            }
            if (page === totalPages && Array.isArray(items)) {
              return items;
            }
          } catch (err: any) {
            console.warn(`[PowerService] Page ${page} attempt ${attempt}/${retries} failed:`, err?.message || err);
          }
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, attempt * 300));
          }
        }
        return [];
      };

      // Parallel batch fetching for pages (CHUNK_SIZE = 3 for reliable network throughput)
      const remainingPages: number[] = [];
      for (let p = 2; p <= totalPages; p++) {
        remainingPages.push(p);
      }

      const CHUNK_SIZE = 3;
      for (let i = 0; i < remainingPages.length; i += CHUNK_SIZE) {
        const chunk = remainingPages.slice(i, i + CHUNK_SIZE);
        const chunkResults = await Promise.all(
          chunk.map(async (page) => {
            const items = await fetchPageWithRetry(page, resolvedPageSize || PAGE_SIZE, 3);
            return { page, items };
          })
        );

        chunkResults.forEach(({ page, items }) => {
          if (items && items.length > 0) {
            pageMap.set(page, items);
          }
        });

        totalLoaded = Array.from(pageMap.values()).reduce((acc, curr) => acc + curr.length, 0);
        notifyPowerFetchProgress(
          totalLoaded,
          totalCount,
          `تم جلب ${totalLoaded} من ${totalCount} مشترك...`
        );
      }

      // Targeted Gap Recovery Pass for any dropped or empty pages
      const missingPages: number[] = [];
      for (let p = 1; p <= totalPages; p++) {
        const pItems = pageMap.get(p);
        if (!pItems || (pItems.length === 0 && p < totalPages)) {
          missingPages.push(p);
        }
      }

      if (missingPages.length > 0) {
        notifyPowerFetchProgress(
          totalLoaded,
          totalCount,
          `جاري استكمال وإعادة جلب الصفحات المتبقية (${missingPages.length} صفحة)...`
        );

        for (const page of missingPages) {
          const items = await fetchPageWithRetry(page, resolvedPageSize || PAGE_SIZE, 4);
          if (items && items.length > 0) {
            pageMap.set(page, items);
          }
          totalLoaded = Array.from(pageMap.values()).reduce((acc, curr) => acc + curr.length, 0);
          notifyPowerFetchProgress(
            totalLoaded,
            totalCount,
            `تم جلب ${totalLoaded} من ${totalCount} مشترك...`
          );
        }
      }

      const allLivePortalItems: any[] = [];
      for (let p = 1; p <= totalPages; p++) {
        const pItems = pageMap.get(p) || [];
        allLivePortalItems.push(...pItems);
      }

      if (allLivePortalItems.length >= totalCount) {
        notifyPowerFetchProgress(allLivePortalItems.length, totalCount, `اكتمل جلب كافة المشتركين بنجاح (${allLivePortalItems.length} مشترك).`);
      } else {
        notifyPowerFetchProgress(allLivePortalItems.length, totalCount, `تم جلب ${allLivePortalItems.length} من ${totalCount} مشترك.`);
      }
      
      const itemMap = new Map<string, any>();
      allLivePortalItems.forEach((item: any) => {
        const id = String(item.id || item.self?.id || '');
        if (id && !itemMap.has(id)) {
          const username = item.username || item.displayValue || item.self?.displayValue || '';
          const realRec = getRealPowerRecord(id) || (username ? getRealPowerRecord(username) : undefined);
          const rxPwr = item.ontRxPower || item.ont_rx_power || item.opticalRxPower || item.optical_rx_power || null;
          const txPwr = item.ontTxPower || item.ont_tx_power || item.opticalTxPower || item.optical_tx_power || null;

          itemMap.set(id, {
            ...item,
            fdt_code: item.fdt_code || item.fdtCode || realRec?.fdt_code,
            fat_code: item.fat_code || item.fatCode || realRec?.fat_code,
            ont_serial: item.ont_serial || item.ontSerial || item.serialNumber || null,
            ont_vendor: (typeof item.ontVendor === 'object' ? item.ontVendor?.displayValue || item.ontVendor?.name : item.ontVendor) || item.ont_vendor || item.vendor || null,
            optical_rx_power: rxPwr,
            optical_tx_power: txPwr,
            power_state: item.power_state || item.powerState || (rxPwr ? 'up' : 'not_scanned')
          });
        }
      });

      const portalItems = Array.from(itemMap.values());

      if (portalItems.length > 0) {
        const subscribers = portalItems.map((item: any) => {
          const subId = item.id || item.self?.id ? String(item.id || item.self?.id) : String(Math.random());
          const username = item.username || item.displayValue || item.self?.displayValue || '';
          const customerName = item.customer?.displayValue || item.customerName || username || 'مشترك';
          const zoneCode = item.zone?.displayValue || item.zone?.id || item.zoneCode || 'FNS0028';
          
          const realRec = getRealPowerRecord(subId) ||
                          getRealPowerRecord(username) ||
                          getRealPowerRecord(customerName) ||
                          item;

          const isExpiredDate = item.expires || item.expiresAt || realRec?.expires_at;
          const hasNotExpiredYet = isExpiredDate ? new Date(isExpiredDate) > new Date() : true;
          const statusStr = item.status?.displayValue || (hasNotExpiredYet || realRec?.status === 'Active' ? 'Active' : 'Expired');
          const isActive = (realRec?.status === 'Active') || statusStr === 'Active' || item.status === 0 || hasNotExpiredYet;

          let fdtCode = item.fdt_code || item.fdtCode || realRec?.fdt_code;
          let fatCode = item.fat_code || item.fatCode || realRec?.fat_code;
          let ontSerial = item.ont_serial || item.ontSerial || item.serialNumber || null;
          let ontVendor = (typeof item.ontVendor === 'object' ? item.ontVendor?.displayValue || item.ontVendor?.name : item.ontVendor) || item.ont_vendor || item.vendor || null;

          if (!fdtCode || !fatCode) {
            const parsed = parseFdtFatFromUsername(username, String(zoneCode));
            if (!fdtCode) fdtCode = parsed.fdtCode;
            if (!fatCode) fatCode = parsed.fatCode;
          }

          if (!ontSerial || ontSerial === 'N/A') ontSerial = null;
          if (!ontVendor || ontVendor === 'N/A') ontVendor = null;

          const initRx = item.optical_rx_power || null;
          const initTx = item.optical_tx_power || undefined;
          let pState = item.power_state || 'not_scanned';
          if (initRx && pState === 'not_scanned') {
            const pVal = parseFloat(initRx);
            if (initRx === 'Offline' || initRx === 'Down') pState = 'down';
            else if (!isNaN(pVal)) pState = pVal <= -27.0 ? 'critical' : 'up';
          }

          return {
            agent_name: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user?.username || 'المقاول'),
            agent_id: activePartnerId || 'user',
            subscription_id: String(subId),
            customer_id: item.customer?.id ? String(item.customer.id) : '',
            customer_name: customerName,
            zone_code: String(zoneCode),
            fdt_code: fdtCode,
            fat_code: fatCode,
            username: String(username),
            status: isActive ? 'Active' : 'Expired',
            expires_at: item.expires || item.expiresAt || '',
            optical_rx_power: initRx || '--',
            optical_tx_power: initTx,
            power_state: pState,
            check_status: initRx ? (pState === 'down' ? 'FAILED' : 'SUCCESS') : 'NOT_SCANNED',
            ont_serial: ontSerial,
            ont_vendor: ontVendor,
            has_active_session: Boolean(item.hasActiveSession || isActive),
            powerReading: initRx ? { optical_rx_power: initRx, power_state: pState, updated_at: new Date().toISOString() } : null,
            updated_at: new Date().toISOString()
          };
        });

        const fats = buildFatGroupsFromSubs(subscribers, activePartnerId);
        const result = { subscribers, fats };
        portalPowerCache = result;
        portalPowerCacheAt = Date.now();

        if (typeof window !== 'undefined' && subscribers.length > 500) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify(result));
          } catch (e) {}
        }

        return result;
      }
    } catch (err) {
      console.error('[PowerService] Portal subscriptions fetch error:', err);
    } finally {
      portalPowerFetchPromise = null;
    }

    if (portalPowerCache && portalPowerCache.subscribers.length > 0) {
      return portalPowerCache;
    }

    return { subscribers: [], fats: [] };
  })();

  return portalPowerFetchPromise;
}

export const powerService = {
  getOverview: async () => {
    const { fats } = await fetchPortalPowerData();
    if (fats.length > 0) return { data: { items: fats } };

    const { user } = useAuthStore.getState();
    if (user?.username || user?.partnerId) {
      return { data: { items: [] } };
    }

    const mock = getMockPowerOverview();
    return { data: { items: mock?.items || [] } };
  },
  getSubscribers: async () => {
    const { subscribers } = await fetchPortalPowerData();
    if (subscribers.length > 0) return { data: { items: subscribers } };

    const { user } = useAuthStore.getState();
    if (user?.username || user?.partnerId) {
      return { data: { items: [] } };
    }

    const mock = getMockPowerSubscribers();
    return { data: { items: mock?.items || [] } };
  },
  getSyncStatus: () => apiClient.get<{ isSyncing: boolean }>('/power/sync-status'),
  checkSubscriberPower: (subscriptionId: string, forceRefresh = false) => 
    apiClient.post('/power/check-subscriber', { subscriptionId, forceRefresh }),
  checkSubscriber: async (subscriptionId: string, _forceRefresh = false) => {
    try {
      const getEntityDisplay = (item: any): string => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return item.displayValue || item.name || item.code || item.fatNumber || item.title || item.id || '';
      };

      // 1. GET /subscriptions/:id
      const subRes = await apiClient.get(`/subscriptions/${subscriptionId}`).catch(() => null);
      const subData = subRes?.data?.model || subRes?.data;
      
      // 2. GET /customers/:customerId
      let customerData: any = null;
      const customerId = subData?.customer?.id || subData?.customerId;
      if (customerId) {
        const custRes = await apiClient.get(`/customers/${customerId}`).catch(() => null);
        customerData = custRes?.data?.model || custRes?.data;
      }

      // 3. GET /subscriptions/:id/device (Canonical source for ONT device specs)
      const devRes = await subscriptionService.getDevice(subscriptionId).catch(() => null);
      const devData = devRes?.data?.model || devRes?.data;

      // 4. Resolve final PPPoE username
      const subscriptionUsername = subData?.username || subData?.self?.displayValue || subData?.displayValue || null;
      const deviceUsername = devData?.username || devData?.self?.displayValue || null;
      const username = deviceUsername || subscriptionUsername || '';

      // 5. GET /subscriber-sessions?username=...
      let sessionData: any = null;
      if (username) {
        const sessRes = await apiClient.get('/subscriber-sessions', { username }).catch(() => null);
        const sessItems = sessRes?.data?.items || (Array.isArray(sessRes?.data) ? sessRes.data : []);
        if (sessItems.length > 0) sessionData = sessItems[0];
      }

      // 6 & 7. Live ONT Power query matching CustomersPage canonical flow
      let ontRes: any = null;
      if (username && username !== '—') {
        try {
          ontRes = await subscriptionService.getDeviceOntStatus(username);
        } catch (err) {
          console.warn("[PowerService] getDeviceOntStatus by username failed, falling back to subId:", err);
        }
      }
      if (!ontRes?.data && subscriptionId) {
        try {
          ontRes = await subscriptionService.getDeviceOnt(subscriptionId);
        } catch (err) {
          console.warn("[PowerService] getDeviceOnt by subId failed:", err);
        }
      }

      const ontModel = ontRes?.data?.model || ontRes?.data;
      const ontStatusObj = ontModel?.status;
      const ontStatusStr = typeof ontStatusObj === 'object'
        ? String(ontStatusObj?.displayValue || ontStatusObj?.name || ontStatusObj?.id || '').toLowerCase()
        : String(ontStatusObj || ontModel?.status || '').toLowerCase();
      const ontStatusId = typeof ontStatusObj === 'object' ? ontStatusObj?.id : undefined;

      const isOntDown = ontStatusStr === 'down' || ontStatusStr === 'offline' || ontStatusId === 1;
      const isOntUp = ontStatusStr === 'up' || ontStatusStr === 'online' || ontStatusId === 0;

      // Extract raw optical Rx power across all possible portal telemetry aliases
      const rawRxPower = ontModel?.rxPower ?? ontModel?.rx_power ?? ontModel?.power ?? ontModel?.rxPowerDbm ?? ontModel?.rx ?? null;
      const rawTxPower = ontModel?.txPower ?? ontModel?.tx_power ?? ontModel?.powerTx ?? ontModel?.txPowerDbm ?? ontModel?.tx ?? null;

      let rxPowerNum: number | null = null;
      if (typeof rawRxPower === 'number') {
        rxPowerNum = rawRxPower;
      } else if (rawRxPower !== null && rawRxPower !== undefined && rawRxPower !== '' && rawRxPower !== '--') {
        const parsed = parseFloat(String(rawRxPower).replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed)) rxPowerNum = parsed;
      }

      let rxPower = 'Unavailable';
      let powerState: 'up' | 'critical' | 'down' | 'unavailable' = 'unavailable';

      if (rxPowerNum !== null) {
        rxPower = `${rxPowerNum.toFixed(2)} dBm`;
        if (isOntDown) {
          powerState = 'down';
        } else {
          powerState = rxPowerNum <= -27.0 ? 'critical' : 'up';
        }
      } else if (isOntDown) {
        powerState = 'down';
        rxPower = 'Offline';
      } else if (isOntUp) {
        powerState = 'up';
        rxPower = 'Online';
      } else if (ontModel) {
        powerState = 'unavailable';
        rxPower = 'Unavailable';
      }

      let txPower: string | null = null;
      if (typeof rawTxPower === 'number') {
        txPower = `${rawTxPower.toFixed(2)} dBm`;
      } else if (rawTxPower) {
        txPower = String(rawTxPower).includes('dBm') ? String(rawTxPower) : `${rawTxPower} dBm`;
      }

      const realRec = getRealPowerRecord(subscriptionId) ||
                      (username ? getRealPowerRecord(username) : undefined) ||
                      (customerData?.self?.displayValue ? getRealPowerRecord(customerData.self.displayValue) : undefined);

      const isExpiredDate = subData?.expires || realRec?.expires_at;
      const hasNotExpiredYet = isExpiredDate ? new Date(isExpiredDate) > new Date() : true;
      const isActive = (realRec?.status === 'Active') || subData?.status?.displayValue === 'Active' || subData?.status === 0 || hasNotExpiredYet;

      const ontVendor = getEntityDisplay(devData?.ontVendor) || devData?.vendor || getEntityDisplay(subData?.ontVendor) || null;
      const ontSerial = devData?.ontSerial || devData?.serialNumber || devData?.serial || subData?.ontSerial || null;
      const fdtCode = getEntityDisplay(devData?.fdt) || subData?.fdtCode || parseFdtFatFromUsername(username, subData?.zone?.displayValue).fdtCode || realRec?.fdt_code || null;
      const fatCode = getEntityDisplay(devData?.fat) || subData?.fatCode || parseFdtFatFromUsername(username, subData?.zone?.displayValue).fatCode || realRec?.fat_code || null;

      return {
        data: {
          subscriptionId: String(subscriptionId),
          customerId: String(customerId || realRec?.customer_id || ''),
          customerName: customerData?.self?.displayValue || subData?.customer?.displayValue || realRec?.customer_name || 'مشترك',
          zoneName: subData?.zone?.displayValue || realRec?.zone_code || null,
          fdtCode,
          fatCode,
          username: username || realRec?.username || '',
          status: isActive ? 'Active' : 'Expired',
          expiresAt: subData?.expires || realRec?.expires_at || '',
          opticalRxPower: rxPower,
          opticalTxPower: txPower,
          powerState: powerState,
          hasActiveSession: Boolean(sessionData || realRec?.has_active_session || isActive),
          ontVendor,
          ontSerial,
          address: customerData?.address || customerData?.primaryContact?.address || null,
          packageName: subData?.planBundle?.displayValue || subData?.planName || null,
          sessionIp: sessionData?.ipAddress || null,
          sessionMac: sessionData?.macAddress || null,
          totalUploadedBytes: sessionData?.totalUploadedBytes || 0,
          totalDownloadedBytes: sessionData?.totalDownloadedBytes || 0,
          alerts: powerState === 'critical' 
            ? ['⚠️ إشارة الضوء ضعيفة (أقل من -27 dBm) - يرجى فحص الكيبل الضوئي']
            : (powerState === 'down' ? ['🔴 جهاز ONT غير متصل بالخدمة حالياً (Down)'] : [])
        }
      };
    } catch (err: any) {
      return {
        data: {
          subscriptionId,
          powerState: 'unavailable',
          status: 'error',
          alerts: ['فشل الاتصال بالبوابة للتحقق من تفاصيل المشترك']
        }
      };
    }
  },
  checkSubscriberOnt: async (subscriptionId: string, username?: string): Promise<{
    rxPower: string | null;
    txPower: string | null;
    powerState: 'up' | 'critical' | 'down' | 'unavailable';
    status: string;
    isOntDown: boolean;
    isCritical: boolean;
  }> => {
    try {
      let ontRes: any = null;
      if (username && username !== '—') {
        try {
          ontRes = await subscriptionService.getDeviceOntStatus(username);
        } catch (err) {}
      }
      if (!ontRes?.data && subscriptionId) {
        try {
          ontRes = await subscriptionService.getDeviceOnt(subscriptionId);
        } catch (err) {}
      }

      const ontModel = ontRes?.data?.model || ontRes?.data;
      if (!ontModel) {
        return {
          rxPower: null,
          txPower: null,
          powerState: 'unavailable',
          status: 'unavailable',
          isOntDown: false,
          isCritical: false
        };
      }

      const ontStatusObj = ontModel?.status;
      const ontStatusStr = typeof ontStatusObj === 'object'
        ? String(ontStatusObj?.displayValue || ontStatusObj?.name || ontStatusObj?.id || '').toLowerCase()
        : String(ontStatusObj || ontModel?.status || '').toLowerCase();
      const ontStatusId = typeof ontStatusObj === 'object' ? ontStatusObj?.id : undefined;

      const isOntDown = ontStatusStr === 'down' || ontStatusStr === 'offline' || ontStatusId === 1;
      const isOntUp = ontStatusStr === 'up' || ontStatusStr === 'online' || ontStatusId === 0;

      const rawRxPower = ontModel?.rxPower ?? ontModel?.rx_power ?? ontModel?.power ?? ontModel?.rxPowerDbm ?? ontModel?.rx ?? null;
      const rawTxPower = ontModel?.txPower ?? ontModel?.tx_power ?? ontModel?.powerTx ?? ontModel?.txPowerDbm ?? ontModel?.tx ?? null;

      let rxPowerNum: number | null = null;
      if (typeof rawRxPower === 'number') {
        rxPowerNum = rawRxPower;
      } else if (rawRxPower !== null && rawRxPower !== undefined && rawRxPower !== '' && rawRxPower !== '--') {
        const parsed = parseFloat(String(rawRxPower).replace(/[^\d.-]/g, ''));
        if (!isNaN(parsed)) rxPowerNum = parsed;
      }

      let txPower: string | null = null;
      if (typeof rawTxPower === 'number') {
        txPower = `${rawTxPower.toFixed(2)} dBm`;
      } else if (rawTxPower) {
        txPower = String(rawTxPower).includes('dBm') ? String(rawTxPower) : `${rawTxPower} dBm`;
      }

      let rxPower: string | null = null;
      let powerState: 'up' | 'critical' | 'down' | 'unavailable' = 'unavailable';
      let isCritical = false;

      if (rxPowerNum !== null) {
        rxPower = `${rxPowerNum.toFixed(2)} dBm`;
        isCritical = rxPowerNum <= -27.0;
        if (isOntDown) {
          powerState = 'down';
        } else {
          powerState = isCritical ? 'critical' : 'up';
        }
      } else if (isOntDown) {
        powerState = 'down';
        rxPower = 'Offline';
      } else if (isOntUp) {
        powerState = 'up';
        rxPower = 'Online';
      }

      return {
        rxPower,
        txPower,
        powerState,
        status: ontStatusStr || (isOntDown ? 'down' : 'up'),
        isOntDown,
        isCritical
      };
    } catch (err) {
      return {
        rxPower: null,
        txPower: null,
        powerState: 'unavailable',
        status: 'error',
        isOntDown: false,
        isCritical: false
      };
    }
  },
  getSubscriberHistory: (subscriptionId: string) => 
    apiClient.get(`/power/subscribers/${subscriptionId}/history`),
  
  // 🚀 NEXT-GEN SERVER-SIDE POWER & OPTICAL READING METHODS:
  getPowerReadings: (params?: { fdt?: string; fat?: string; zone?: string; status?: string; subscriptionIds?: string; limit?: number }) =>
    apiClient.get<{ success: boolean; count: number; readings: any[]; summary: any }>('/power/readings', params),
  
  scanSingleSubscriber: (payload: { subscriptionId: string; username: string; customerName?: string; zoneCode?: string; fdtCode?: string; fatCode?: string; ontSerial?: string; ontVendor?: string; hasActiveSession?: boolean; status?: string }) =>
    apiClient.post<{ success: boolean; reading: any }>('/power/scan-subscriber', payload),
  
  scanFatSubscribers: (payload: { fdtCode?: string; fatCode?: string; subscribers: any[] }) =>
    apiClient.post<{ success: boolean; total: number; successful: number; results: any[] }>('/power/scan-fat', payload),
  
  startScanJob: () =>
    apiClient.post<{ success: boolean; job: any; existing?: boolean }>('/power/scan-jobs'),
  
  getActiveScanJob: () =>
    apiClient.get<{ success: boolean; job: any }>('/power/scan-jobs/active'),

  getLatestScanJob: () =>
    apiClient.get<{ success: boolean; job: any }>('/power/scan-jobs/latest'),
  
  cancelScanJob: (jobId: string) =>
    apiClient.post<{ success: boolean; message: string }>(`/power/scan-jobs/${jobId}/cancel`),

  syncPower: () => apiClient.post('/power/fat/sync'),
  syncAgentPower: (agentId: string | number) => apiClient.post(`/agents/${agentId}/sync/power`),
  syncAgentDetails: (agentId: string | number) => apiClient.post(`/agents/${agentId}/sync/customer-details`),
  invalidateCache: () => {
    portalPowerCache = null;
    portalPowerCacheAt = 0;
    try {
      invalidateLocalPowerCache();
    } catch (e) {}
  }
};

export * from './debts';
export * from './users';
export * from './mockPowerData';
export * from './power-cache-service';
